import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, Building2, Check, Fingerprint, Headphones, Network,
} from 'lucide-react';
import Seo, { ORGANIZATION, SITE_URL } from '../components/Seo';
import Reveal, { prefersReducedMotion } from '../components/Reveal';
import { portalUrl } from '../config/site';
import { HOME } from '../data/home';
import { HOME_PRODUCTS } from '../content/siteContentDefaults';
import { resolveMediaUrl, useSiteContent } from '../content/siteContent';

const USE_CASE_ICONS = [Headphones, Fingerprint, Building2, Network];

/** Internal paths use the router; https links open as normal links. */
const CtaLink = ({ to, className, children }) => (
  to.startsWith('/')
    ? <Link to={to} className={className}>{children}</Link>
    : <a href={to} className={className}>{children}</a>
);

const HeroVideo = ({ src, webm, poster, className }) => {
  const [allowMotion] = useState(() => !prefersReducedMotion());
  if (!allowMotion || !src) {
    return <img src={poster} alt="" className={className} />;
  }
  return (
    <video className={className} poster={poster} autoPlay muted loop playsInline preload="metadata" aria-hidden="true">
      {webm && <source src={webm} type="video/webm" />}
      <source src={src} type="video/mp4" />
    </video>
  );
};

const ProductCard = ({ product }) => (
  <Link
    to={product.to}
    className="group flex h-full flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-[#0a1f2e]/20 hover:shadow-[0_24px_60px_-24px_rgba(10,31,46,0.3)]"
  >
    <div
      className="flex h-56 items-center justify-center"
      style={{ background: 'radial-gradient(ellipse at 50% 60%, rgba(200,255,0,0.22), transparent 62%), linear-gradient(to bottom, #f1f6e6, #ffffff)' }}
    >
      <img
        src={product.crystal}
        alt=""
        loading="lazy"
        className="h-40 w-40 object-contain drop-shadow-[0_18px_24px_rgba(10,31,46,0.25)] transition-transform duration-500 group-hover:-translate-y-1 group-hover:scale-105"
      />
    </div>
    <div className="flex flex-1 flex-col px-7 pb-7 pt-5">
      <div className="flex items-start justify-between gap-4">
        <h3 className="font-display text-xl font-semibold text-[#0a1f2e]">{product.title}</h3>
        <ArrowUpRight className="mt-1 h-5 w-5 shrink-0 text-gray-400 transition-colors group-hover:text-[#0a1f2e]" strokeWidth={1.75} />
      </div>
      <p className="mt-2 flex-1 text-[15px] leading-relaxed text-gray-600">{product.description}</p>
      <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0a1f2e]">
        Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </span>
    </div>
  </Link>
);

const Home = () => {
  const c = useSiteContent('home');
  const products = HOME_PRODUCTS.map((product) => ({
    ...product,
    title: c(`products.${product.slug}.title`),
    description: c(`products.${product.slug}.description`),
  }));
  const companyLogos = [];
  for (let i = 1; i <= HOME.logoSlots; i += 1) {
    const name = c(`logos.${i}.name`);
    const image = resolveMediaUrl(c(`logos.${i}.image`));
    if (name && image) companyLogos.push({ name, image });
  }
  const useCases = HOME.useCases.items.map((u, i) => ({
    ...u,
    icon: USE_CASE_ICONS[i],
    title: c(`usecases.${i + 1}.title`),
    tag: c(`usecases.${i + 1}.tag`),
    body: c(`usecases.${i + 1}.body`),
  }));
  const steps = HOME.steps.items.map((step, i) => ({ verb: c(`steps.${i + 1}.verb`), body: c(`steps.${i + 1}.body`) }));

  const heroPoster = resolveMediaUrl(c('hero.image'));
  const heroVideo = resolveMediaUrl(c('hero.video'));
  // The WebM version only exists for the built-in globe video.
  const heroVideoWebm = c('hero.video') === HOME.hero.video ? HOME.hero.videoWebm : null;
  const highlight = c('hero.highlight');

  return (
    <div className="min-h-screen bg-white text-[#0a1f2e]">
      <Seo
        title="Business Phone Numbers, Cloud PBX & SIP Trunks"
        description="Phone numbers in 140+ countries, Cloud PBX, SIP trunking, AI Receptionist and Voice OTP from VOIP2, a UK voice provider."
        path="/"
        jsonLd={[{ '@context': 'https://schema.org', ...ORGANIZATION },
          { '@context': 'https://schema.org', '@type': 'WebSite', name: 'VOIP2', url: `${SITE_URL}/`, publisher: { '@id': ORGANIZATION['@id'] } }]}
      />

      {/* Hero */}
      <section className="relative isolate flex min-h-[100dvh] items-center overflow-hidden bg-[#0a1f2e] pt-14 text-white">
        <HeroVideo
          src={heroVideo}
          webm={heroVideoWebm}
          poster={heroPoster}
          className="absolute inset-0 -z-20 h-full w-full object-cover object-right"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-r from-[#0a1f2e] via-[#0a1f2e]/75 to-transparent" />

        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Reveal as="h1" className="font-display text-5xl font-semibold leading-[1.05] tracking-tight md:text-6xl lg:text-7xl">
              {c('hero.headline')}{highlight && <> <span className="text-[#c8ff00]">{highlight}</span></>}
            </Reveal>
            <Reveal as="p" delay={120} className="mt-6 max-w-[52ch] text-lg leading-relaxed text-gray-300 md:text-xl">
              {c('hero.sub')}
            </Reveal>
            <Reveal delay={240} className="mt-10 flex flex-col gap-4 sm:flex-row">
              <CtaLink
                to={c('hero.primary_url')}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#c8ff00] px-8 py-4 text-base font-semibold text-[#0a1f2e] transition-transform hover:brightness-95 active:scale-[0.98]"
              >
                {c('hero.primary_label')}
                <ArrowRight className="h-5 w-5" strokeWidth={2} />
              </CtaLink>
              <CtaLink
                to={c('hero.secondary_url')}
                className="inline-flex items-center justify-center rounded-full border border-white/40 bg-[#0a1f2e]/30 px-8 py-4 text-base font-semibold text-white backdrop-blur-sm transition-colors hover:border-white hover:bg-white hover:text-[#0a1f2e] active:scale-[0.98]"
              >
                {c('hero.secondary_label')}
              </CtaLink>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Customer logos (shown only when configured in the CMS) */}
      {companyLogos.length > 0 && (
        <section className="border-y border-gray-100 px-4 py-12 sm:px-6 lg:px-8">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-14 gap-y-8">
            {companyLogos.map((logo) => (
              <img key={logo.image} src={logo.image} alt={logo.name} className="max-h-10 w-auto object-contain opacity-60 grayscale transition hover:opacity-100 hover:grayscale-0" />
            ))}
          </div>
        </section>
      )}

      {/* Products */}
      <section className="bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-14 max-w-2xl">
            <h2 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
              {c('products.title')}
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              {c('products.subtitle')}
            </p>
          </Reveal>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {products.map((product, i) => (
              <Reveal key={product.slug} delay={(i % 3) * 80}>
                <ProductCard product={product} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Platform */}
      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="overflow-hidden rounded-3xl">
              <img src="/media/site/noc.webp" alt="Engineers monitoring a global voice network" loading="lazy" className="aspect-[16/10] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-5">
            <h2 className="font-display text-4xl font-semibold tracking-tight">
              {c('platform.title')}
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-gray-600">
              {c('platform.body')}
            </p>
            <ul className="mt-8 space-y-4">
              {[
                c('platform.bullet_1'), c('platform.bullet_2'), c('platform.bullet_3'),
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#c8ff00]">
                    <Check className="h-4 w-4 text-[#0a1f2e]" strokeWidth={2.5} />
                  </span>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <a
              href={portalUrl('/customer/login')}
              className="mt-10 inline-flex items-center gap-2 font-semibold text-[#0a1f2e] underline decoration-[#c8ff00] decoration-2 underline-offset-4 hover:decoration-[#0a1f2e]"
            >
              {c('platform.link_label')} <ArrowRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* Use cases */}
      <section className="bg-[#f5f7f9] px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-12 max-w-2xl">
            <h2 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
              {c('usecases.title')}
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              {c('usecases.subtitle')}
            </p>
          </Reveal>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {useCases.map((useCase, i) => {
              // Alternate navy and white cards in a checkerboard so the grid reads at a glance.
              const dark = i === 0 || i === 3;
              const UseCaseIcon = useCase.icon;
              return (
                <Reveal
                  key={useCase.links[0][1]}
                  delay={(i % 2) * 80}
                  className={`group relative flex flex-col overflow-hidden rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1 md:p-10 ${dark ? 'bg-[#0a1f2e] text-white shadow-[0_24px_60px_-28px_rgba(10,31,46,0.6)]' : 'border border-gray-200 bg-white hover:border-[#0a1f2e]/30 hover:shadow-[0_24px_60px_-32px_rgba(10,31,46,0.35)]'}`}
                >
                  <UseCaseIcon
                    aria-hidden="true"
                    strokeWidth={1}
                    className={`pointer-events-none absolute -right-8 -top-8 h-48 w-48 transition-transform duration-500 group-hover:rotate-6 group-hover:scale-105 ${dark ? 'text-white/[0.06]' : 'text-[#0a1f2e]/[0.04]'}`}
                  />
                  <div className="relative flex items-center gap-4">
                    <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl ${dark ? 'bg-[#c8ff00] text-[#0a1f2e]' : 'bg-[#0a1f2e] text-[#c8ff00]'}`}>
                      <UseCaseIcon className="h-6 w-6" strokeWidth={1.75} />
                    </span>
                    <span className={`text-xs font-semibold uppercase tracking-[0.14em] ${dark ? 'text-[#c8ff00]' : 'text-gray-500'}`}>{useCase.tag}</span>
                  </div>
                  <h3 className="relative mt-7 font-display text-2xl font-semibold tracking-tight md:text-3xl">{useCase.title}</h3>
                  <p className={`relative mt-3 max-w-[44ch] text-base leading-relaxed ${dark ? 'text-gray-300' : 'text-gray-600'}`}>{useCase.body}</p>
                  <div className="relative mt-auto pt-8">
                    <div className={`flex flex-wrap gap-2 border-t pt-6 ${dark ? 'border-white/10' : 'border-gray-100'}`}>
                      {useCase.links.map(([label, to]) => (
                        <Link
                          key={to}
                          to={to}
                          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${dark ? 'bg-white/10 text-white hover:bg-[#c8ff00] hover:text-[#0a1f2e]' : 'bg-[#f5f7f9] text-[#0a1f2e] hover:bg-[#0a1f2e] hover:text-[#c8ff00]'}`}
                        >
                          {label}
                          <ArrowUpRight className="h-3.5 w-3.5" />
                        </Link>
                      ))}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Getting started */}
      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <Reveal className="mb-14 max-w-2xl">
            <h2 className="font-display text-4xl font-semibold tracking-tight">{c('steps.title')}</h2>
          </Reveal>
          <div className="grid grid-cols-1 gap-10 md:grid-cols-3">
            {steps.map((step, i) => (
              <Reveal key={i} delay={i * 100} className="border-t-2 border-[#c8ff00] pt-6">
                <div className="font-display text-3xl font-semibold">{step.verb}</div>
                <p className="mt-3 max-w-[36ch] text-gray-600">{step.body}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={300} className="mt-12">
            <Link to="/regulatory-requirements" className="inline-flex items-center gap-2 font-semibold text-[#0a1f2e] underline decoration-[#c8ff00] decoration-2 underline-offset-4 hover:decoration-[#0a1f2e]">
              See country requirements <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-4 pb-24 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-7xl rounded-3xl bg-[#0a1f2e] px-6 py-20 text-center text-white">
          <h2 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            {c('cta.title')}
          </h2>
          <p className="mx-auto mt-5 max-w-[52ch] text-lg text-gray-300">
            {c('cta.subtitle')}
          </p>
          <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#c8ff00] px-8 py-4 font-semibold text-[#0a1f2e] transition-transform hover:brightness-95 active:scale-[0.98]">
              {c('cta.primary_label')} <ArrowRight className="h-5 w-5" />
            </Link>
            <Link to="/coverage" className="inline-flex items-center justify-center rounded-full border border-white/40 px-8 py-4 font-semibold text-white transition-colors hover:border-white hover:bg-white hover:text-[#0a1f2e] active:scale-[0.98]">
              {c('cta.secondary_label')}
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Home;
